export const workflowStateTranslations: Record<string, string> = {
  'ACTIVE': 'Aktiv',
  'OPEN': 'Aktiv',
  'FINISHED': 'Abgeschlossen',
  'COMPLETED': 'Abgeschlossen',
  'APPROVED': 'Genehmigt',
  'REJECTED': 'Abgelehnt',
  'ERROR': 'Fehler',
  'CANCELLED': 'Abgebrochen',
  'CANCELED': 'Abgebrochen',
  'TRIGGER_NOT_MATCHED': 'Bedingung nicht erfüllt',
  'TECHNICALLY_FAILED': 'Technischer Fehler',
  'RETRYING_MICROTECH': 'Synchronisierung...',
  'INQUIRY_ANSWERED': 'Rückfrage beantwortet',
  'INQUIRY_TO_ME': 'Rückfrage an mich',
  'INQUIRY_OPEN': 'Rückfrage offen'
}

export const actionTranslations: Record<string, string> = {
  'APPROVE': 'Aufgabe freigegeben',
  'REJECT': 'Aufgabe abgelehnt',
  'CANCEL': 'Workflow abgebrochen',
  'CANCELLED': 'Workflow abgebrochen',
  'CANCELED': 'Workflow abgebrochen',
  'CLAIM': 'Aufgabe übernommen',
  'UNCLAIM': 'Aufgabe abgegeben',
  'CLAIMED': 'Aufgabe übernommen',
  'UNCLAIMED': 'Aufgabe abgegeben',
  'START_INQUIRY': 'Rückfrage gestellt',
  'ANSWER_INQUIRY': 'Rückfrage beantwortet',
  'FORCE_PULL_INQUIRY': 'Rückfrage zurückgeholt',
  'FORCE_PULLED': 'Aufgabe zurückgeholt',
  'NOTE': 'Hinweis hinterlegt',
  'HINWEIS': 'Hinweis hinterlegt',
  'RÜCKFRAGE AUTOMATISCH GESCHLOSSEN': 'Rückfrage automatisch geschlossen',
  'INQUIRY_AUTO_CLOSED': 'Rückfrage automatisch geschlossen',
  'START': 'Start',
  'WORKFLOW STARTED': 'Workflow gestartet',
  'STEP COMPLETED': 'Schritt abgeschlossen',
  'WORKFLOW TRIGGER CONDITION NOT MATCHED': 'Startbedingung nicht erfüllt',
  'WORKFLOW SUCCESSFULLY COMPLETED (TRIGGER CONDITION NOT MATCHED)': 'Workflow abgeschlossen (Bedingung nicht erfüllt)',
  'AUFGABEN-ÜBERNAHME ZEITÜBERSCHREITUNG': 'Aufgaben-Übernahme Zeitüberschreitung',
  'RÜCKFRAGE ZEITÜBERSCHREITUNG': 'Rückfrage Zeitüberschreitung'
}

export type WorkflowStateColor = 'success' | 'error' | 'warning' | 'primary' | 'secondary' | 'neutral' | 'pink' | 'purple' | 'blue'

export function getWorkflowStateColor(state?: string | null): WorkflowStateColor {
  if (!state) return 'neutral'
  const s = state.toUpperCase()

  // Genehmigt / Abgeschlossen: Grün
  if (['DONE', 'COMPLETED', 'APPROVED', 'SUCCESS', 'FINISHED'].includes(s)) {
    return 'success'
  }

  // Abgelehnt / Fehler / Technischer Fehler: Rot
  if (['REJECTED', 'FAILED', 'ERROR', 'TECHNICALLY_FAILED'].includes(s)) {
    return 'error'
  }

  // Abgebrochen: Grau
  if (['CANCELLED', 'CANCELED'].includes(s)) {
    return 'neutral'
  }

  // Bedingung nicht erfüllt: Orange
  if (s === 'TRIGGER_NOT_MATCHED') {
    return 'warning'
  }

  // Rückfrage gestellt / an mich / offen: Pink
  if (['INQUIRY_TO_ME', 'INQUIRY_OPEN'].includes(s)) {
    return 'pink'
  }

  // Rückfrage beantwortet: Violett
  if (s === 'INQUIRY_ANSWERED') {
    return 'purple'
  }

  // Synchronisation mit microtech: Orange (warning)
  if (s === 'RETRYING_MICROTECH') {
    return 'warning'
  }

  // Aktiv / Offen / In Bearbeitung: Blau (secondary)
  if (['IN_PROGRESS', 'OPEN', 'ACTIVE', 'BLUE', 'SECONDARY'].includes(s)) {
    return 'secondary'
  }

  return 'neutral'
}

export function getActionColor(action?: string | null, explicitColor?: string | null): WorkflowStateColor {
  if (explicitColor) {
    const c = explicitColor.toLowerCase()
    if (['success', 'error', 'warning', 'primary', 'secondary', 'neutral', 'pink', 'purple', 'violet', 'blue'].includes(c)) {
      if (c === 'violet') return 'purple'
      if (c === 'blue') return 'secondary'
      return c as WorkflowStateColor
    }
    if (c === 'green') return 'success'
    if (c === 'red') return 'error'
    if (c === 'orange' || c === 'amber') return 'warning'
    if (c === 'gray' || c === 'grey') return 'neutral'
    if (c === 'blue') return 'secondary'
  }

  if (!action) return 'neutral'
  const upper = action.toUpperCase()
  if (upper === 'APPROVE' || upper === 'AUFGABE FREIGEGEBEN' || upper.includes('SUCCESSFULLY COMPLETED')) return 'success'
  if (upper === 'REJECT' || upper === 'AUFGABE ABGELEHNT') return 'error'
  if (upper === 'CANCEL' || upper === 'CANCELLED' || upper === 'CANCELED' || upper === 'WORKFLOW ABGEBROCHEN') return 'neutral'
  if (upper === 'ANSWER_INQUIRY' || upper === 'RÜCKFRAGE BEANTWORTET') return 'purple'
  if (upper.includes('TRIGGER') || upper.includes('BEDINGUNG')) return 'warning'
  if (upper.includes('INQUIRY') || upper.includes('RÜCKFRAGE')) return 'pink'
  return 'neutral'
}

export function translateWorkflowState(state: string): string {
  if (!state) return ''
  const s = state.toUpperCase()
  if (s.startsWith('INQUIRY_TO:')) {
    return `Rückfrage an: ${state.substring('INQUIRY_TO:'.length)}`
  }
  return workflowStateTranslations[s] || state
}

export function translateAction(action: string): string {
  if (!action) return ''
  const upperAction = action.toUpperCase()

  // Exact match
  if (actionTranslations[upperAction]) {
    return actionTranslations[upperAction]
  }

  // Pattern matching
  if (upperAction.startsWith('STEP STARTED:')) {
    const type = upperAction.replace('STEP STARTED:', '').trim()
    if (type === 'USERSTEP') return 'Benutzerschritt gestartet'
    if (type === 'SYSTEMSTEP') return 'Systemschritt gestartet'
    return 'Schritt gestartet: ' + type
  }

  if (upperAction.startsWith('SYSTEM TASK:')) {
    const task = action.substring('System task:'.length).trim()
    return 'Systemaufgabe: ' + task
  }

  if (upperAction.startsWith('WORKFLOW TRIGGERED BY')) {
    const source = action.substring('Workflow triggered by'.length).trim()
    return 'Workflow gestartet durch ' + source
  }

  return actionTranslations[upperAction] || action
}
